/**
 * Decide how a placement reaches its spot — self-park vs towed — from the area's
 * parking policy and the motion config. Pure: same inputs → same procedure.
 */
import type { AircraftCatalog, EventPlacement, ZoneGeometry } from '../engine/types'
import type { MotionConfig, ParkingMode, Procedure } from '../data/motionConfig'

/**
 * Effective parking policy for an area: the authored area setting
 * (`areas.parking_mode` → `zone.parkingMode`) wins, then the motion config's
 * per-area override, then the global default.
 */
function modeForArea(areaId: string, cfg: MotionConfig, zone?: ZoneGeometry): ParkingMode {
  return zone?.parkingMode ?? cfg.perAreaParkingMode[areaId] ?? cfg.defaultParkingMode
}

export function resolveProcedure(
  p: EventPlacement,
  catalog: AircraftCatalog,
  cfg: MotionConfig,
  zone?: ZoneGeometry
): Procedure {
  if (!cfg.enableTow) return 'self_park'

  const mode = modeForArea(p.areaId, cfg, zone)
  if (mode === 'self_park') return 'self_park'
  if (mode === 'tow_only') return 'drop_then_tow'

  // mode === 'both' — pick by the configured rule.
  switch (cfg.bothModeRule) {
    case 'always_tow':
      return 'drop_then_tow'
    case 'always_self':
      return 'self_park'
    case 'by_size':
    case 'by_vip': {
      // by_vip falls back to size until VIP is threaded onto placements.
      // The authored area threshold wins over the global one when present.
      const wingspan = catalog.get(p.typeCode)?.footprint.wingspan ?? 0
      const threshold = zone?.towMinWingspanFt ?? cfg.towWingspanFt
      return wingspan >= threshold ? 'drop_then_tow' : 'self_park'
    }
    default:
      return 'self_park'
  }
}

/** Resolve procedures for every placement, keyed by eventAllocationId. */
export function resolveAllProcedures(
  placements: Record<string, EventPlacement>,
  catalog: AircraftCatalog,
  cfg: MotionConfig,
  zones?: ZoneGeometry[]
): Record<string, Procedure> {
  const zoneById = new Map<string, ZoneGeometry>()
  if (zones) for (const z of zones) zoneById.set(z.areaId, z)
  const out: Record<string, Procedure> = {}
  for (const [id, p] of Object.entries(placements)) {
    out[id] = resolveProcedure(p, catalog, cfg, zoneById.get(p.areaId))
  }
  return out
}
